import {
  HighchartsChartDataSeriesOption,
  HighchartsChartDataSeriesOptionDataType,
  HighchartsChartDataType,
} from "@/storybook/c8r-highcharts/common/utils/types/common";
import {
  DateDataPoint,
  DateLabel,
  ForecastedCostRe,
} from "@/services/cloudchipr.api";
import { formatDateByFrequencyType } from "@/utils/helpers/date-time/datetime-format";

export const addForecastedCostIntoBillingExplorerChartData = (
  chartData: HighchartsChartDataType,
  dateLabel: Extract<DateLabel, "quarter_to_date" | "month_to_date">,
  dataPoint: DateDataPoint,
  labels: Record<string, string> | null,
  forecastedCosts?: ForecastedCostRe,
  quarterStartMonth?: number,
): {
  chartDataWithForecast: HighchartsChartDataType;
  labelsWithForecast?: Record<string, string> | null;
} => {
  const labelsWithForecast = { ...labels };

  const forecastedCost = forecastedCosts?.find((fc) =>
    dateLabel.includes(fc.forecast_option.toLowerCase()),
  );
  const dailyCosts = forecastedCost?.daily_costs;

  if (!dailyCosts) {
    return { chartDataWithForecast: chartData, labelsWithForecast: labels };
  }

  const dates = dailyCosts.map(({ date }) => {
    const dateName = formatDateByFrequencyType(
      dataPoint,
      date,
      quarterStartMonth,
    );

    labelsWithForecast[dateName] = date;

    return dateName;
  });

  const forecastedCostData: HighchartsChartDataSeriesOptionDataType[] =
    dailyCosts.map(({ cost }) => ({ y: cost }));

  let filteredCount = 0;

  const filteredCategories = chartData.categories.filter((category) => {
    if (!dates.includes(category)) {
      return true;
    }

    filteredCount = filteredCount + 1;
    return false;
  });

  const nullCosts: HighchartsChartDataSeriesOptionDataType[] = Array(
    filteredCategories.length,
  ).fill({ y: null });

  const forecasted: HighchartsChartDataSeriesOption = {
    name: forecastSeriesName,
    data: [...nullCosts, ...forecastedCostData],
    color: "rgba(158,163,197,0.32)",
    marker: { enabled: true },
    borderColor: "#7986CB",
    borderWidth: 1,
  };

  const filteredSeries = chartData.series.map((series) => {
    if (!filteredCount) {
      return series;
    }

    return {
      ...series,
      data: series.data?.slice(0, series.data.length - filteredCount),
    };
  });

  return {
    chartDataWithForecast: {
      ...chartData,
      categories: [...filteredCategories, ...dates],
      series: dates.length ? [...filteredSeries, forecasted] : chartData.series,
    },
    labelsWithForecast,
  };
};

export const forecastSeriesName = "Forecasted";
